import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Search, 
  Users,
  Mail,
  Phone,
  MapPin,
  Trash2,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Arbres",
  "Arbustes",
  "Plantes",
  "Gazon",
  "Béton",
  "Pierres",
  "Clôtures",
  "Mobilier",
  "Éclairage",
  "Arrosage",
  "Terrassement",
  "Général"
];

export default function Suppliers() {
  const { getAuthHeaders } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    email: "",
    category: "",
    phone: "",
    address: ""
  });

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, { headers: getAuthHeaders() });
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        `${API}/suppliers`,
        newSupplier,
        { headers: getAuthHeaders() }
      );
      setSuppliers(prev => [response.data, ...prev]);
      setAddOpen(false);
      setNewSupplier({
        name: "",
        email: "",
        category: "",
        phone: "",
        address: ""
      });
      toast.success("Fournisseur ajouté");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/suppliers/${deleteId}`, { headers: getAuthHeaders() });
      setSuppliers(prev => prev.filter(s => s.id !== deleteId));
      toast.success("Fournisseur supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    s.category.toLowerCase().includes(search.toLowerCase())
  );

  const groupedByCategory = filteredSuppliers.reduce((acc, s) => {
    const cat = s.category || "Autre";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Fournisseurs
            </h1>
            <p className="text-gray-500 mt-1">
              {suppliers.length} fournisseur{suppliers.length > 1 ? "s" : ""} enregistré{suppliers.length > 1 ? "s" : ""}
            </p>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="add-supplier-btn">
                <Plus className="h-5 w-5" />
                Ajouter un fournisseur
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau fournisseur</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de l'entreprise *</Label>
                  <Input
                    value={newSupplier.name}
                    onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                    placeholder="Ex: Pépinières Martin SA"
                    required
                    data-testid="supplier-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                    placeholder="contact@pepinieres-martin.ch"
                    required
                    data-testid="supplier-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Catégorie *</Label>
                  <Select 
                    value={newSupplier.category}
                    onValueChange={(val) => setNewSupplier({ ...newSupplier, category: val })}
                  >
                    <SelectTrigger data-testid="supplier-category">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                    placeholder="+41 22 123 45 67"
                    data-testid="supplier-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={newSupplier.address}
                    onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                    placeholder="Route de Genève 42, 1003 Lausanne"
                    data-testid="supplier-address"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="save-supplier-btn">
                    Ajouter
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Rechercher un fournisseur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
            data-testid="search-suppliers"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 w-3/4 bg-gray-200 rounded mb-4" />
                  <div className="h-4 w-1/2 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-1/3 bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search ? "Aucun résultat" : "Aucun fournisseur"}
              </h3>
              <p className="text-gray-500 mb-6">
                {search 
                  ? "Essayez une autre recherche" 
                  : "Ajoutez vos premiers fournisseurs pour envoyer des demandes de devis"}
              </p>
              {!search && (
                <Button onClick={() => setAddOpen(true)} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un fournisseur
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByCategory).map(([category, categorySuppliers]) => (
              <div key={category}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#1E8B5A]" />
                  {category}
                  <span className="text-sm font-normal text-gray-500">
                    ({categorySuppliers.length})
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categorySuppliers.map((supplier) => (
                    <Card key={supplier.id} className="card-hover group">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(supplier.id)}
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <h3 className="font-semibold text-gray-900 mb-3">
                          {supplier.name}
                        </h3>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <a 
                              href={`mailto:${supplier.email}`}
                              className="hover:text-[#1E8B5A] truncate"
                            >
                              {supplier.email}
                            </a>
                          </div>
                          {supplier.phone && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <a 
                                href={`tel:${supplier.phone}`}
                                className="hover:text-[#1E8B5A]"
                              >
                                {supplier.phone}
                              </a>
                            </div>
                          )}
                          {supplier.address && (
                            <div className="flex items-start gap-2 text-gray-600">
                              <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                              <span className="truncate">{supplier.address}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce fournisseur ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
